// Republic Atlas - API Client Configuration
// Enhanced error handling for better developer experience

import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import type { ApiError } from '@/types';

// =============================================================================
// Configuration
// =============================================================================

// Suppress verbose logging in production
const VERBOSE_LOGGING = import.meta.env.DEV;
const SUPPRESS_NETWORK_ERRORS = !import.meta.env.DEV; // Don't spam console in prod

// API base URL from environment
const getApiBaseUrl = () => {
  // In development on localhost, use relative URL to leverage Vite proxy
  if (import.meta.env.DEV && typeof window !== 'undefined' && 
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
    return '/api/v1'; // Relative URL - Vite will proxy to http://localhost:8000
  }
  
  // If VITE_API_URL is explicitly set, use it (highest priority)
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  
  // In production builds, default to Docker cluster if available, otherwise fallback to GCP
  if (import.meta.env.PROD) {
    // Check for Docker cluster URL
    const dockerClusterUrl = import.meta.env.VITE_DOCKER_CLUSTER_URL;
    if (dockerClusterUrl) {
      // Ensure it includes /api/v1
      return dockerClusterUrl.endsWith('/api/v1') 
        ? dockerClusterUrl 
        : `${dockerClusterUrl}/api/v1`;
    }
    // Fallback to GCP for backward compatibility (will be removed once Docker cluster is stable)
    return 'https://republic-atlas-api-946143380554.us-central1.run.app/api/v1';
  }
  
  // Development fallback
  return 'https://republic-atlas-api-946143380554.us-central1.run.app/api/v1';
};

const API_BASE_URL = getApiBaseUrl();

// Log API configuration once on startup (dev only)
if (VERBOSE_LOGGING) {
  console.log('[API] Configured base URL:', API_BASE_URL);
}

// =============================================================================
// Axios Instance
// =============================================================================

export const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// =============================================================================
// Request Interceptor
// =============================================================================

api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Get token from Firebase Auth (if available)
    try {
      const { getAuth } = await import('firebase/auth');
      const { initializeApp, getApps } = await import('firebase/app');
      
      const firebaseConfig = {
        apiKey: import.meta.env.VITE_FIREBASE_API_KEY || '',
        authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || '',
        projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || '',
        storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || '',
        messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '',
        appId: import.meta.env.VITE_FIREBASE_APP_ID || '',
      };
      
      const app = getApps().length > 0 ? getApps()[0] : initializeApp(firebaseConfig);
      const auth = getAuth(app);
      
      if (auth.currentUser) {
        const token = await auth.currentUser.getIdToken();
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch {
      // Fallback to localStorage if Firebase not available
      const token = localStorage.getItem('auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    
    // Log request with full URL
    if (VERBOSE_LOGGING) {
      const fullUrl = `${config.baseURL || ''}${config.url || ''}`;
      console.log(`[API] → ${config.method?.toUpperCase()} ${fullUrl}`, config.params || '');
    }
    
    return config;
  },
  (error) => {
    console.error('[API] Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// =============================================================================
// Response Interceptor - Enhanced Error Handling
// =============================================================================

// Track repeated errors to avoid console spam
const errorTracker = new Map<string, { count: number; lastTime: number }>();
const ERROR_THROTTLE_MS = 5000; // Only log same error every 5 seconds
const ERROR_THROTTLE_COUNT = 3; // After 3 identical errors, stop logging

function shouldLogError(errorKey: string): boolean {
  const now = Date.now();
  const tracked = errorTracker.get(errorKey);
  
  if (!tracked) {
    errorTracker.set(errorKey, { count: 1, lastTime: now });
    return true;
  }
  
  // Reset if enough time has passed
  if (now - tracked.lastTime > ERROR_THROTTLE_MS * 10) {
    errorTracker.set(errorKey, { count: 1, lastTime: now });
    return true;
  }
  
  // Increment count
  tracked.count++;
  tracked.lastTime = now;
  
  // Stop logging after threshold
  if (tracked.count > ERROR_THROTTLE_COUNT) {
    return false;
  }
  
  // Throttle repeated errors
  return now - tracked.lastTime > ERROR_THROTTLE_MS;
}

api.interceptors.response.use(
  (response) => {
    if (VERBOSE_LOGGING) {
      const dataSize = JSON.stringify(response.data).length;
      console.log(`[API] ← ${response.status} ${response.config.url} (${(dataSize / 1024).toFixed(1)}KB)`);
    }
    return response;
  },
  async (error: AxiosError<ApiError>) => {
    const originalRequest = error.config;
    const url = originalRequest?.url || 'unknown';
    const method = originalRequest?.method?.toUpperCase() || 'UNKNOWN';
    
    // Handle 401 - token expired
    if (error.response?.status === 401 && originalRequest) {
      if (VERBOSE_LOGGING) {
        console.warn(`[API] ← 401 ${method} ${url} - Token expired`);
      }
      localStorage.removeItem('auth_token');
      // Only redirect if not already on login page
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
      return Promise.reject({
        message: 'Session expired. Please sign in again.',
        code: 'AUTH_EXPIRED',
        silent: true, // Don't show toast for auth redirect
      });
    }
    
    // Handle CORS errors specifically
    if (error.message === 'Network Error' && !error.response) {
      const errorKey = `cors:${url}`;
      
      if (!SUPPRESS_NETWORK_ERRORS && shouldLogError(errorKey)) {
        console.warn(`[API] ← CORS/Network Error ${method} ${url}`);
      }
      
      return Promise.reject({
        message: 'Unable to connect to server. The API may be temporarily unavailable.',
        code: 'CONNECTION_ERROR',
        details: { url, type: 'cors_or_network' },
      });
    }
    
    // Handle network errors (no response)
    if (!error.response) {
      const errorKey = `network:${url}`;
      
      if (!SUPPRESS_NETWORK_ERRORS && shouldLogError(errorKey)) {
        console.warn(`[API] ← Network Error ${method} ${url}:`, error.message);
      }
      
      return Promise.reject({
        message: 'Network error. Please check your connection.',
        code: 'NETWORK_ERROR',
        details: { url, originalMessage: error.message },
      });
    }
    
    // Handle server errors (5xx)
    if (error.response.status >= 500) {
      const errorKey = `server:${error.response.status}:${url}`;
      
      if (shouldLogError(errorKey)) {
        console.error(`[API] ← ${error.response.status} ${method} ${url} - Server Error`);
      }
      
      return Promise.reject({
        message: 'Server error. Please try again later.',
        code: 'SERVER_ERROR',
        status: error.response.status,
        details: { url },
      });
    }
    
    // Handle client errors (4xx)
    if (error.response.status >= 400) {
      // Log 4xx errors with details
      if (VERBOSE_LOGGING) {
        console.warn(`[API] ← ${error.response.status} ${method} ${url}:`, error.response.data);
      }
      
      return Promise.reject({
        message: error.response.data?.message 
          || error.response.data?.details 
          || 'Request failed',
        code: `HTTP_${error.response.status}`,
        status: error.response.status,
        details: error.response.data,
      });
    }
    
    // Return API error response for any other case
    return Promise.reject(error.response?.data || {
      message: 'An unexpected error occurred',
      code: 'UNKNOWN_ERROR',
    });
  }
);

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Transform API error to user-friendly message
 */
export function getErrorMessage(error: unknown): string {
  if (typeof error === 'object' && error !== null) {
    if ('message' in error && typeof (error as ApiError).message === 'string') {
      return (error as ApiError).message;
    }
    if ('detail' in error && typeof (error as { detail: string }).detail === 'string') {
      return (error as { detail: string }).detail;
    }
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred';
}

/**
 * Check if error should be shown to user (not silent)
 */
export function shouldShowError(error: unknown): boolean {
  if (typeof error === 'object' && error !== null && 'silent' in error) {
    return !(error as { silent: boolean }).silent;
  }
  return true;
}

/**
 * Check if error is a connection/network error
 */
export function isConnectionError(error: unknown): boolean {
  if (typeof error === 'object' && error !== null && 'code' in error) {
    const code = (error as { code: string }).code;
    return code === 'NETWORK_ERROR' || code === 'CONNECTION_ERROR';
  }
  return false;
}

export default api;

